import { EventBus } from '../core/EventBus';
import { ISceneAdapter } from '../engine/ISceneAdapter';
import { DataValidator } from '../utils/DataValidator';
import { EnemyFactory } from '../factories/EnemyFactory';
import { ContentRegistry, GameSettingsContent, loadContentRegistry, WaveContent, WaveSpawnContent } from '../data';
import { EnemyScalingResolver, EnemyScalingSummary } from './EnemyScalingResolver';
import { createLogger } from '../utils/Logger';
import {
  applyRuntimeTestingOverrides,
  DEFAULT_RUNTIME_TESTING_MODE_STATE,
  RuntimeTestingModeState
} from '../config/RuntimeTestingMode';
import {
  createDebugCommandFailure,
  createDebugCommandSuccess,
  DebugCommandResult,
  DebugWaveActionSnapshot,
  ForceWaveDebugPayload,
  resolveDebugCommandSource,
  SkipWaveDebugPayload,
  WaveDebugResult
} from './DebugCommandTypes';

// Типы для конфигурации волн
type SpawnConfig = WaveSpawnContent;
type WaveConfig = WaveContent;

/**
  * Класс системы спавна волн
  * Управляет таймингом волн и спавном врагов
  */
export class WaveSpawnerSystem {
  private enemyFactory: EnemyFactory;
  private eventBus: EventBus;
  private waves: readonly WaveConfig[] = [];
  private currentTime: number = 0;
  private currentWaveIndex: number = 0;
  private wavesStarted: number = 0;
  private elapsedRunTime: number = 0;
  private isSpawning: boolean = false;
  private validator: DataValidator;
  private isBossActive: boolean = false;
  private bossesSpawned: number = 0;
  private sceneAdapter: ISceneAdapter;
  private contentRegistry: ContentRegistry;
  private baseGameSettings: GameSettingsContent;
  private effectiveGameSettings: GameSettingsContent;
  private runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE;
  private enemyScalingResolver: EnemyScalingResolver;
  private lastResolvedSpawns: Array<{
    waveIndex: number;
    enemyId: string;
    modifierIds: readonly string[];
    resolvedStats: EnemyScalingSummary['resolvedStats'];
  }> = [];
  private lastDebugWaveAction: DebugWaveActionSnapshot | null = null;
  private readonly waveEnemyTokens = new Map<object, number>();
  private readonly activeWaveEnemyTokens = new Set<number>();
  private nextWaveEnemyToken: number = 1;
  private readonly logger = createLogger('WaveSpawnerSystem');
  private readonly onEnemyKilled = (event: { data?: { enemy?: unknown } }) => this.handleEnemyKilled(event);

  /**
   * Создает систему спавна волн
   * @param enemyFactory - фабрика врагов
   * @param eventBus - шина событий
   * @param sceneAdapter - адаптер сцены
   */
  constructor(
    enemyFactory: EnemyFactory,
    eventBus: EventBus,
    sceneAdapter: ISceneAdapter,
    contentRegistry: ContentRegistry = loadContentRegistry(),
    runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE
  ) {
    this.enemyFactory = enemyFactory;
    this.eventBus = eventBus;
    this.sceneAdapter = sceneAdapter;
    this.contentRegistry = contentRegistry;
    this.baseGameSettings = this.contentRegistry.getGameSettings();
    this.runtimeTestingMode = runtimeTestingMode;
    this.effectiveGameSettings = applyRuntimeTestingOverrides(this.baseGameSettings, this.runtimeTestingMode, this.logger);
    this.validator = new DataValidator();
    this.enemyScalingResolver = new EnemyScalingResolver(this.logger);
    this.eventBus.on('enemy:killed', this.onEnemyKilled);
  }

  public setRuntimeTestingMode(runtimeTestingMode: RuntimeTestingModeState): void {
    if (!runtimeTestingMode) {
      this.logger.warn('setRuntimeTestingMode:invalid-state', { runtimeTestingMode });
      return;
    }

    this.runtimeTestingMode = runtimeTestingMode;
    this.effectiveGameSettings = applyRuntimeTestingOverrides(this.baseGameSettings, runtimeTestingMode, this.logger);
    this.logger.info('setRuntimeTestingMode:applied', {
      enabled: runtimeTestingMode.enabled,
      pauseWaves: runtimeTestingMode.pauseWaves,
      earlyBoss: runtimeTestingMode.earlyBoss,
      bossSpawnTime: this.effectiveGameSettings.bossSpawnTime,
      bossesToWin: this.effectiveGameSettings.bossesToWin
    });
  }

  /**
    * Загружает конфигурацию волн из JSON
    * @param wavesData - данные волн
    */
  public loadWaves(wavesData: readonly WaveConfig[]): void {
    this.logger.debug('loadWaves:start', { waves: wavesData.length });
    const validation = this.validator.validate('wave', wavesData);
    if (!validation.isValid) {
      this.logger.error('loadWaves:invalid-wave-table', { errors: validation.errors });
      return;
    }

    this.waves = [...wavesData];
    this.currentWaveIndex = 0;
    this.currentTime = 0;
    this.wavesStarted = 0;
    this.elapsedRunTime = 0;
    this.isSpawning = false;
    this.bossesSpawned = 0;
    this.isBossActive = false;
    this.lastResolvedSpawns = [];
    this.lastDebugWaveAction = null;
    this.clearActiveWaveEnemies();
    this.logger.info('loadWaves:complete', {
      waves: this.waves.length,
      bossSpawnTime: this.effectiveGameSettings.bossSpawnTime,
      bossesToWin: this.effectiveGameSettings.bossesToWin
    });
  }

  /**
   * Обновляет состояние системы спавна волн
   * @param dt - дельта времени
   */
  public update(dt: number): void {
    if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.pauseWaves) {
      return;
    }

    if (this.waves.length === 0) return;
    this.elapsedRunTime += dt;

    // Stop processing waves if boss is active
    if (this.isBossActive) {
      return;
    }

    // Если волны закончились, но включен повтор — сбрасываем цикл
    if (this.currentWaveIndex >= this.waves.length) {
      if (this.waves.length > 0) {
        this.logger.debug('update:waves-complete-looping', {
          waves: this.waves.length, wavesStarted: this.wavesStarted, elapsedRunTime: this.elapsedRunTime
        });
        this.lastResolvedSpawns = [];
        this.currentWaveIndex = 0;
        this.currentTime = 0;
      } else {
        return;
      }
    }

    this.currentTime += dt;

    // Проверяем, пора ли запускать следующую волну
    const currentWave = this.waves[this.currentWaveIndex];
    if (currentWave && this.currentTime >= currentWave.time && !this.isSpawning) {
      const spawnSummary = currentWave.spawns.map(spawn => ({
        enemyId: spawn.enemyId,
        count: spawn.count,
        modifiers: spawn.modifiers
      }));
      this.logger.debug('update:starting-wave', {
        waveIndex: this.currentWaveIndex,
        currentTime: this.currentTime.toFixed(1),
        wavesStarted: this.wavesStarted,
        elapsedRunTime: this.elapsedRunTime,
        spawnSummary
      });
      this.startWave();
    }
  }

  /**
    * Запускает спавн текущей волны
    */
  private startWave(): void {
    const currentWave = this.waves[this.currentWaveIndex];
    if (!currentWave) {
      return;
    }
    this.isSpawning = true;

    // Запускаем спавн всех врагов в волне
    for (const spawn of currentWave.spawns) {
      this.spawnEnemies(spawn);
    }
    this.wavesStarted += 1;
    this.eventBus.publish({ type: 'wave:started', data: { waveNumber: this.wavesStarted } });

    // Отмечаем волну как завершенную после спавна всех врагов
    this.completeWave();
  }

  /**
   * Спавнит врагов по заданной конфигурации
   * @param spawnConfig - конфигурация спавна
   */
  private spawnEnemies(spawnConfig: SpawnConfig): void {
    const enemyContent = this.contentRegistry.findEnemy(spawnConfig.enemyId);
    if (!enemyContent) {
      this.logger.warn('spawnEnemies:unknown-enemy-skipped', {
        waveIndex: this.currentWaveIndex,
        enemyId: spawnConfig.enemyId
      });
      return;
    }

    const scalingSummary = this.enemyScalingResolver.resolve(
      enemyContent,
      spawnConfig,
      this.wavesStarted,
      this.elapsedRunTime
    );
    if (!this.isResolvedStatsUsable(scalingSummary)) {
      this.logger.warn('spawnEnemies:invalid-scaling-summary-skipped', {
        waveIndex: this.currentWaveIndex,
        enemyId: spawnConfig.enemyId,
        resolvedStats: scalingSummary.resolvedStats
      });
      return;
    }
    const { width, height } = this.sceneAdapter.getSceneBounds();
    const positions = this.getSpawnPositions(spawnConfig.pattern, spawnConfig.count, width, height, spawnConfig.params);
    const structuredModifierNames = spawnConfig.scaling ? Object.keys(spawnConfig.scaling) : [];
    const modifierIds = [...spawnConfig.modifiers, ...structuredModifierNames];
    this.recordScalingSummary(scalingSummary, modifierIds);

    this.logger.debug('spawnEnemies:resolved-scaling', {
      waveIndex: this.currentWaveIndex,
      enemyId: spawnConfig.enemyId,
      count: spawnConfig.count,
      modifierIds,
      resolvedStats: scalingSummary.resolvedStats
    });

    for (const pos of positions) {
      // Используем фабрику врагов
      const enemy = this.enemyFactory.create(spawnConfig.enemyId, pos.x, pos.y, {
        resolvedStats: scalingSummary.resolvedStats,
        scalingMetadata: {
          waveIndex: this.wavesStarted,
          elapsedWaveTime: this.elapsedRunTime,
          modifierIds
        }
      });

      if (enemy) {
        this.registerWaveEnemy(enemy);
        this.eventBus.publish({
          type: 'enemy:spawned',
          data: { enemy, enemyId: spawnConfig.enemyId }
        });
      }
    }
  }

  private getSpawnPositions(pattern: string, count: number, width: number, height: number, params?: Readonly<Record<string, unknown>>): { x: number, y: number }[] {
    const positions: { x: number, y: number }[] = [];
    const padding = 50;
    const scroll = this.sceneAdapter.getCameraScroll();

    // Adjust Center/Bounds based on Camera Scroll
    const centerX = scroll.x + width / 2;
    const centerY = scroll.y + height / 2;
    const minX = scroll.x;
    const maxX = scroll.x + width;
    const minY = scroll.y;
    const maxY = scroll.y + height;

    switch (pattern) {
      case 'circle':
        // Use custom radius if provided, otherwise default calculation
        const defaultRadius = Math.min(width, height) / 2 + padding;
        const radius = typeof params?.radius === 'number' ? params.radius : defaultRadius;

        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          positions.push({
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          });
        }
        break;

      case 'cluster':
        let clusterX = 0;
        let clusterY = 0;
        const side = Math.floor(Math.random() * 4);
        // Random spot on edge relative to camera
        switch (side) {
          case 0: clusterX = minX + Math.random() * width; clusterY = minY - padding; break; // Top
          case 1: clusterX = maxX + padding; clusterY = minY + Math.random() * height; break; // Right
          case 2: clusterX = minX + Math.random() * width; clusterY = maxY + padding; break; // Bottom
          case 3: clusterX = minX - padding; clusterY = minY + Math.random() * height; break; // Left
        }

        for (let i = 0; i < count; i++) {
          positions.push({
            x: clusterX + (Math.random() - 0.5) * 100,
            y: clusterY + (Math.random() - 0.5) * 100
          });
        }
        break;

      case 'random':
      default:
        for (let i = 0; i < count; i++) {
          let x = 0;
          let y = 0;
          const s = Math.floor(Math.random() * 4);
          switch (s) {
            case 0: x = minX + Math.random() * width; y = minY - padding; break;
            case 1: x = maxX + padding; y = minY + Math.random() * height; break;
            case 2: x = minX + Math.random() * width; y = maxY + padding; break;
            case 3: x = minX - padding; y = minY + Math.random() * height; break;
          }
          positions.push({ x, y });
        }
        break;
    }

    return positions;
  }

  /**
   * Завершает текущую волну и переходит к следующей
   */
  private completeWave(): void {
    this.isSpawning = false;

    // Публикуем событие о завершении волны
    this.eventBus.publish({
      type: 'wave:complete',
      data: {
        waveIndex: this.currentWaveIndex,
        time: this.currentTime
      }
    });

    this.currentWaveIndex++;
  }

  private registerWaveEnemy(enemy: unknown): void {
    if (!this.isObjectLike(enemy)) {
      return;
    }

    const token = this.nextWaveEnemyToken++;
    this.activeWaveEnemyTokens.add(token);
    this.waveEnemyTokens.set(enemy, token);

    const sprite = this.resolveEnemySprite(enemy);
    if (sprite) {
      this.waveEnemyTokens.set(sprite, token);
    }
  }

  private handleEnemyKilled(event: { data?: { enemy?: unknown } }): void {
    const token = this.resolveWaveEnemyToken(event.data?.enemy);
    if (token === undefined) {
      return;
    }

    this.activeWaveEnemyTokens.delete(token);
    for (const [key, value] of this.waveEnemyTokens.entries()) {
      if (value === token) {
        this.waveEnemyTokens.delete(key);
      }
    }

    if (this.activeWaveEnemyTokens.size === 0) {
      this.startNextWaveImmediatelyAfterClear();
    }
  }

  private resolveWaveEnemyToken(enemy: unknown): number | undefined {
    if (!this.isObjectLike(enemy)) {
      return undefined;
    }

    const directToken = this.waveEnemyTokens.get(enemy);
    if (directToken !== undefined) {
      return directToken;
    }

    const entity = this.resolveEnemyEntity(enemy);
    if (entity) {
      return this.waveEnemyTokens.get(entity);
    }

    const sprite = this.resolveEnemySprite(enemy);
    if (sprite) {
      return this.waveEnemyTokens.get(sprite);
    }

    return undefined;
  }

  private startNextWaveImmediatelyAfterClear(): void {
    if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.pauseWaves) {
      return;
    }

    if (this.isBossActive || this.isSpawning || this.waves.length === 0) {
      return;
    }

    if (this.currentWaveIndex >= this.waves.length) {
      this.logger.debug('wave-clear:waves-complete-looping', {
        waves: this.waves.length, wavesStarted: this.wavesStarted, elapsedRunTime: this.elapsedRunTime
      });
      this.lastResolvedSpawns = [];
      this.currentWaveIndex = 0;
      this.currentTime = 0;
    }

    const nextWave = this.waves[this.currentWaveIndex];
    if (!nextWave) {
      return;
    }

    this.currentTime = Math.max(this.currentTime, nextWave.time);
    this.logger.debug('wave-clear:starting-next-wave', {
      waveIndex: this.currentWaveIndex,
      wavesStarted: this.wavesStarted,
      elapsedRunTime: this.elapsedRunTime,
      currentTime: this.currentTime.toFixed(1)
    });
    this.startWave();
  }

  private clearActiveWaveEnemies(): void {
    this.waveEnemyTokens.clear();
    this.activeWaveEnemyTokens.clear();
  }

  private isObjectLike(value: unknown): value is object {
    return (typeof value === 'object' || typeof value === 'function') && value !== null;
  }

  private resolveEnemySprite(enemy: object): object | undefined {
    const maybeEntity = enemy as { getSprite?: () => unknown };
    const sprite = typeof maybeEntity.getSprite === 'function' ? maybeEntity.getSprite() : undefined;
    return this.isObjectLike(sprite) ? sprite : undefined;
  }

  private resolveEnemyEntity(enemy: object): object | undefined {
    const maybeSprite = enemy as { getData?: (key: string) => unknown };
    const entity = typeof maybeSprite.getData === 'function' ? maybeSprite.getData('entity') : undefined;
    return this.isObjectLike(entity) ? entity : undefined;
  }

  /**
   * Возвращает текущий индекс волны
   */
  public getWaveIndex(): number {
    return this.currentWaveIndex;
  }

  public dispose(): void {
    this.eventBus.off('enemy:killed', this.onEnemyKilled);
    this.clearActiveWaveEnemies();
    this.logger.debug('dispose:complete');
  }

  /**
   * Проверяет, завершена ли последняя волна
   */
  public isLastWaveCompleted(): boolean {
    return this.currentWaveIndex >= this.waves.length;
  }

  public getDiagnosticsSnapshot(): {
    currentWaveIndex: number;
    elapsedWaveTime: number;
    wavesStarted: number;
    elapsedRunTime: number;
    bossActive: boolean;
    bossesSpawned: number;
    pauseWaves: boolean;
    effectiveBossSettings: {
      bossSpawnTime: number;
      bossesToWin: number;
      bossEnemyId: string;
      source: 'production' | 'testing';
    };
    scalingSummary?: {
      waveIndex: number;
      enemyId: string;
      modifierIds: readonly string[];
      resolvedStats: {
        health: number;
        maxHealth: number;
        speed: number;
        damage: number;
        xpReward: number;
      };
    };
    lastDebugWaveAction: DebugWaveActionSnapshot | null;
  } {
    return {
      currentWaveIndex: this.currentWaveIndex,
      elapsedWaveTime: this.currentTime,
      wavesStarted: this.wavesStarted,
      elapsedRunTime: this.elapsedRunTime,
      bossActive: this.isBossActive,
      bossesSpawned: this.bossesSpawned,
      pauseWaves: this.runtimeTestingMode.enabled && this.runtimeTestingMode.pauseWaves,
      effectiveBossSettings: {
        bossSpawnTime: this.effectiveGameSettings.bossSpawnTime,
        bossesToWin: this.effectiveGameSettings.bossesToWin,
        bossEnemyId: this.effectiveGameSettings.bossEnemyId,
        source: this.runtimeTestingMode.enabled ? 'testing' : 'production'
      },
      scalingSummary: this.lastResolvedSpawns.length > 0 ? this.lastResolvedSpawns[this.lastResolvedSpawns.length - 1] : undefined,
      lastDebugWaveAction: this.lastDebugWaveAction
    };
  }

  public skipToNextWaveForTesting(payload: SkipWaveDebugPayload = {}): DebugCommandResult<WaveDebugResult> {
    const source = resolveDebugCommandSource(payload.source);
    const command = 'skipWave' as const;
    this.logger.debug('skipToNextWaveForTesting:input', {
      source,
      currentWaveIndex: this.currentWaveIndex,
      elapsedWaveTime: this.currentTime
    });

    if (!this.runtimeTestingMode.enabled) {
      this.logger.warn('skipToNextWaveForTesting:testing-disabled', { source });
      return this.createBlockedWaveCommandFailure(command, 'skip', source, 'testing-disabled');
    }

    if (this.waves.length === 0) {
      this.logger.warn('skipToNextWaveForTesting:waves-unloaded', { source });
      return this.createBlockedWaveCommandFailure(command, 'skip', source, 'waves-unloaded');
    }

    if (this.isBossActive) {
      this.logger.warn('skipToNextWaveForTesting:boss-pause-active', {
        source,
        currentWaveIndex: this.currentWaveIndex
      });
      return this.createBlockedWaveCommandFailure(command, 'skip', source, 'boss-pause-active');
    }

    const previousWaveIndex = ((this.currentWaveIndex % this.waves.length) + this.waves.length) % this.waves.length;
    const nextWaveIndex = (previousWaveIndex + 1) % this.waves.length;
    const nextWave = this.waves[nextWaveIndex];
    if (!nextWave) {
      this.logger.error('skipToNextWaveForTesting:missing-next-wave', {
        source,
        previousWaveIndex,
        nextWaveIndex
      });
      return this.createBlockedWaveCommandFailure(command, 'skip', source, 'execution-failed', 'Failed to resolve next wave.');
    }

    this.currentWaveIndex = nextWaveIndex;
    this.currentTime = nextWave.time;
    this.isSpawning = false;
    this.lastResolvedSpawns = [];
    this.lastDebugWaveAction = this.createDebugWaveAction('skip', source, previousWaveIndex, nextWaveIndex, 'accepted');
    const result = createDebugCommandSuccess('skipWave', source, {
      previousWaveIndex,
      nextWaveIndex,
      elapsedWaveTime: this.currentTime,
      lastDebugWaveAction: this.lastDebugWaveAction
    });
    this.logger.info('skipToNextWaveForTesting:accepted', {
      source,
      previousWaveIndex,
      nextWaveIndex,
      elapsedWaveTime: this.currentTime
    });
    return result;
  }

  public forceWaveForTesting(payload?: ForceWaveDebugPayload): DebugCommandResult<WaveDebugResult> {
    const source = resolveDebugCommandSource(payload?.source);
    const command = 'forceWave' as const;
    this.logger.debug('forceWaveForTesting:input', {
      source,
      waveIndex: payload?.waveIndex,
      currentWaveIndex: this.currentWaveIndex,
      elapsedWaveTime: this.currentTime
    });

    if (!this.runtimeTestingMode.enabled) {
      this.logger.warn('forceWaveForTesting:testing-disabled', { source });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'testing-disabled');
    }

    if (this.waves.length === 0) {
      this.logger.warn('forceWaveForTesting:waves-unloaded', { source });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'waves-unloaded');
    }

    if (!payload || typeof payload !== 'object' || !Number.isInteger(payload.waveIndex)) {
      this.logger.warn('forceWaveForTesting:invalid-wave-index-payload', {
        source,
        waveIndex: payload?.waveIndex
      });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'invalid-payload', 'waveIndex must be an integer.');
    }

    if (payload.waveIndex < 0 || payload.waveIndex >= this.waves.length) {
      this.logger.warn('forceWaveForTesting:wave-index-out-of-range', {
        source,
        waveIndex: payload.waveIndex,
        waveCount: this.waves.length
      });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'wave-index-out-of-range', 'waveIndex is out of range.', {
        waveIndex: payload.waveIndex,
        waveCount: this.waves.length
      });
    }

    if (this.isBossActive) {
      this.logger.warn('forceWaveForTesting:boss-pause-active', {
        source,
        currentWaveIndex: this.currentWaveIndex
      });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'boss-pause-active');
    }

    const previousWaveIndex = this.currentWaveIndex;
    const nextWaveIndex = payload.waveIndex;
    const forcedWave = this.waves[nextWaveIndex];
    if (!forcedWave) {
      this.logger.error('forceWaveForTesting:missing-target-wave', {
        source,
        waveIndex: nextWaveIndex
      });
      return this.createBlockedWaveCommandFailure(command, 'force', source, 'execution-failed', 'Target wave is missing.');
    }

    this.currentWaveIndex = nextWaveIndex;
    this.currentTime = forcedWave.time;
    this.isSpawning = false;
    this.lastResolvedSpawns = [];
    this.lastDebugWaveAction = this.createDebugWaveAction('force', source, previousWaveIndex, nextWaveIndex, 'accepted');
    const result = createDebugCommandSuccess('forceWave', source, {
      previousWaveIndex,
      nextWaveIndex,
      elapsedWaveTime: this.currentTime,
      lastDebugWaveAction: this.lastDebugWaveAction
    });
    this.logger.info('forceWaveForTesting:accepted', {
      source,
      previousWaveIndex,
      nextWaveIndex,
      elapsedWaveTime: this.currentTime
    });
    return result;
  }

  public setBossPauseState(isBossActive: boolean, state?: { bossesSpawned?: number }): void {
    this.isBossActive = isBossActive;
    if (typeof state?.bossesSpawned === 'number' && Number.isFinite(state.bossesSpawned)) {
      this.bossesSpawned = Math.max(0, Math.floor(state.bossesSpawned));
    }

    this.logger.info('setBossPauseState:applied', {
      isBossActive: this.isBossActive,
      bossesSpawned: this.bossesSpawned
    });
  }

  private isResolvedStatsUsable(summary: EnemyScalingSummary): boolean {
    const stats = summary.resolvedStats;
    return Number.isFinite(stats.health)
      && Number.isFinite(stats.maxHealth)
      && Number.isFinite(stats.speed)
      && Number.isFinite(stats.damage)
      && Number.isFinite(stats.xpReward)
      && stats.health >= 1
      && stats.maxHealth >= 1
      && stats.speed >= 0
      && stats.damage >= 0
      && stats.xpReward >= 0;
  }

  private recordScalingSummary(summary: EnemyScalingSummary, modifierIds: readonly string[]): void {
    this.lastResolvedSpawns.push({
      waveIndex: summary.waveIndex,
      enemyId: summary.enemyId,
      modifierIds,
      resolvedStats: summary.resolvedStats
    });

    if (this.lastResolvedSpawns.length > 8) {
      this.lastResolvedSpawns.splice(0, this.lastResolvedSpawns.length - 8);
    }
  }

  private createDebugWaveAction(
    action: DebugWaveActionSnapshot['action'],
    source: DebugWaveActionSnapshot['source'],
    previousWaveIndex: number,
    nextWaveIndex: number,
    result: DebugWaveActionSnapshot['result'],
    reason?: DebugWaveActionSnapshot['reason']
  ): DebugWaveActionSnapshot {
    return {
      action,
      source,
      previousWaveIndex,
      nextWaveIndex,
      result,
      reason,
      timestampMs: Date.now()
    };
  }

  private createBlockedWaveCommandFailure(
    command: 'skipWave' | 'forceWave',
    action: DebugWaveActionSnapshot['action'],
    source: DebugWaveActionSnapshot['source'],
    reason: NonNullable<DebugWaveActionSnapshot['reason']>,
    message?: string,
    details?: unknown
  ) {
    this.lastDebugWaveAction = this.createDebugWaveAction(
      action,
      source,
      this.currentWaveIndex,
      this.currentWaveIndex,
      'blocked',
      reason
    );
    return createDebugCommandFailure(command, source, reason, message, details);
  }
}

export default WaveSpawnerSystem;
